import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { TextField, Button, Paper, Box, Typography } from '@mui/material';
import { Col, Row } from '../Grid';
import { useThemeMode } from '../../utils/useThemeMode';

const SearchForm = ({ onSubmitForm, errorMessage }) => {
  const { mode } = useThemeMode();
  
  // React logo blue color for accents
  const reactBlue = 'rgba(97, 218, 251, 0.9)';
  
  // Dynamic form styling based on theme
  const FormStyle = {
    background: mode === 'dark' ? '#0A1929' : '#fff',
    color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit',
    borderRadius: '15px',
    paddingLeft: '30px',
    paddingRight: '30px',
    paddingTop: '30px',
    paddingBottom: '30px',
    boxShadow: mode === 'dark' 
      ? `0px 0px 10px rgba(97, 218, 251, 0.2)` 
      : '0px 0px 10px rgba(0, 0, 0, 0.1)',
    border: mode === 'dark' ? `1px solid rgba(97, 218, 251, 0.1)` : 'none',
    width: '100%',
    maxWidth: '100%'
  };

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    defaultValues: {
      keywords: '',
      location: ''
    }
  });

  const textFieldStyle = {
    marginBottom: '20px',
    marginTop: '10px',
    input: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.87)' : 'inherit' 
    },
    label: { 
      color: mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'inherit' 
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.3)' : undefined
      },
      "&:hover fieldset": {
        borderColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.5)' : undefined
      },
      "&.Mui-focused fieldset": {
        borderColor: mode === 'dark' ? reactBlue : undefined
      }
    }
  };

  const buttonStyle = {
    marginTop: '10px',
    padding: '10px 24px',
    fontSize: '1rem',
    backgroundColor: mode === 'dark' ? reactBlue : undefined,
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(97, 218, 251, 0.8)' : undefined
    },
    boxShadow: mode === 'dark' ? '0 4px 12px rgba(97, 218, 251, 0.25)' : undefined,
    transition: 'all 0.2s ease-in-out',
  };

  const onSubmit = data => {
    onSubmitForm(data);
  };

  return (
    <Col size="12 col-md-12 lg-8 mx-auto">
      <Typography 
        variant="h4" 
        className="text-center mt-5 pt-4 montserrat"
        sx={{ 
          fontWeight: 'bold',
          color: 'inherit'
        }}
      >
        Time to start the hunt!
      </Typography>
      
      <Typography 
        variant="subtitle1" 
        className="text-center"
        sx={{ 
          marginBottom: '1.5rem',
          color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit'
        }}
      >
        Let's hunt for some jobs! Add jobs to your progress tracker
      </Typography>
      
      <Box component={Paper} elevation={3} sx={FormStyle}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row className="mb-3">
            <Col size="12 md-6">
              <Controller
                name="keywords"
                control={control}
                rules={{ required: 'Keywords are required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Keywords"
                    variant="outlined"
                    fullWidth
                    error={!!errors.keywords}
                    helperText={errors.keywords?.message || ''}
                    sx={textFieldStyle}
                  />
                )}
              />
            </Col>
            <Col size="12 md-6">
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Locations"
                    variant="outlined"
                    fullWidth
                    sx={textFieldStyle}
                  />
                )}
              />
            </Col>
          </Row>
          
          {errorMessage && (
            <Typography 
              color="error" 
              sx={{ marginBottom: '10px' }}
            >
              {errorMessage}
            </Typography>
          )}
          
          <Row className="justify-content-end">
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={buttonStyle}
            >
              Search
            </Button>
          </Row>
        </form>
      </Box>
    </Col>
  );
};

export default SearchForm;
